import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { filter, finalize, map, startWith, switchMap, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  Abonnement,
  AjoutParametragesSpecifiquesAbonnementRequetePayload,
  AjoutParametragesSpecifiquesEvenementRequetePayload,
  AjoutParametragesSpecifiquesPackRequetePayload,
  Client,
  Evenement,
  Pack,
  ParametragesGlobauxClientCalculCommission,
  ParametrageSpecifiqueAbonnement,
  ParametrageSpecifiqueEvenement,
  ParametrageSpecifiquePack,
  ParametragesSpecifiquesCalculCommissionAbonnement,
  ParametragesSpecifiquesCalculCommissionEvenement,
  ParametragesSpecifiquesCalculCommissionPack,
} from './commissions.interface.';

// Etat d'ecran (client choisi, filtres, panneaux ouverts), fourni par la route `parametrage/commissions`
@Injectable()
export class CommissionsService {
  private _httpClient = inject(HttpClient);
  voirPanelAjoutParametrageSpecifiqueEvenement = signal<boolean>(false);
  voirPanelAjoutParametrageSpecifiqueAbonnement = signal<boolean>(false);
  voirPanelAjoutParametrageSpecifiquePack = signal<boolean>(false);

  evenements = signal<Evenement[]>([]);
  abonnements = signal<Abonnement[]>([]);
  packs = signal<Pack[]>([]);

  clientSelection = signal<Client | undefined>(undefined);

  filtreParametrageSpecifiqueEvenement = signal<string>('');
  filtreParametrageSpecifiqueAbonnement = signal<string>('');
  filtreParametrageSpecifiquePack = signal<string>('');

  parametrageSpecifiqueEvenementASupprimer = signal<ParametrageSpecifiqueEvenement | undefined>(undefined);
  parametrageSpecifiqueEvenementAAjouter = signal<AjoutParametragesSpecifiquesEvenementRequetePayload | undefined>(
    undefined,
  );

  parametrageSpecifiqueAbonnementASupprimer = signal<ParametrageSpecifiqueAbonnement | undefined>(undefined);
  parametrageSpecifiqueAbonnementAAjouter = signal<AjoutParametragesSpecifiquesAbonnementRequetePayload | undefined>(
    undefined,
  );

  parametrageSpecifiquePackASupprimer = signal<ParametrageSpecifiquePack | undefined>(undefined);
  parametrageSpecifiquePackAAjouter = signal<AjoutParametragesSpecifiquesPackRequetePayload | undefined>(undefined);

  private clients$ = this._httpClient.get<Client[]>(`${environment.backendUrl}redditions/liste-clients`);
  // Liste complète : depuis la migration au kit, la recherche est interne à `app-select`.
  // Le filtre que portait le service n'était plus alimenté par personne
  clients = toSignal(this.clients$, { initialValue: [] as Client[] });

  // `app-select` ne rend que la clé choisie, et cette clé est le CODE client : le libellé
  // est une donnée métier qui peut être portée par deux clients, `find` retiendrait alors
  // toujours le premier. La résolution appartient au service, qui seul détient la liste
  selectionnerClientParCode(code: string | undefined): void {
    this.clientSelection.set(code ? this.clients().find(client => client.codeClient === code) : undefined);
  }

  parametragesSpecifiquesEvenementFiltres = computed(() => {
    const parametrages = this.parametragesSpecifiquesEvenement();
    const filtre = this.filtreParametrageSpecifiqueEvenement();
    if (parametrages) {
      return parametrages.parametrages.filter(c => c.nomEvenement.toLowerCase().includes(filtre.toLowerCase()));
    }
    return [];
  });

  parametragesSpecifiquesAbonnementFiltres = computed(() => {
    const parametrages = this.parametragesSpecifiquesAbonnement();
    const filtre = this.filtreParametrageSpecifiqueAbonnement();
    if (parametrages) {
      return parametrages.parametrages.filter(c => c.nom.toLowerCase().includes(filtre.toLowerCase()));
    }
    return [];
  });

  parametragesSpecifiquesPackFiltres = computed(() => {
    const parametrages = this.parametragesSpecifiquesPack();
    const filtre = this.filtreParametrageSpecifiquePack();
    if (parametrages) {
      return parametrages.parametrages.filter(c => c.nom.toLowerCase().includes(filtre.toLowerCase()));
    }
    return [];
  });

  /* Les quatre listes de paramétrage sont rechargées à CHAQUE changement de client. Un
     compteur plutôt qu'un booléen : une réponse arrivée tôt ferait passer le tableau en
     « abouti » alors que les trois autres requêtes sont encore en vol. `switchMap` annule
     la précédente et `finalize` décompte aussi sur annulation.
     Le `startWith(undefined)` de chaque flux vide la liste dès le changement de client :
     sans lui, `toSignal` conserve la réponse du client PRÉCÉDENT, et ses règles de
     commission restaient affichées sous le nom du nouveau client — actions de ligne
     comprises. */
  private readonly requetesParametragesEnVol = signal(0);

  chargementParametragesEnCours = computed(() => this.requetesParametragesEnVol() > 0);

  parametragesGlobauxClient = toSignal(
    toObservable(this.clientSelection).pipe(
      filter(c => !!c),
      switchMap((client: Client) => {
        const href = `${environment.backendUrl}redditions/parametrage-calcul-commission/${client.codeClient}/global`;

        this.requetesParametragesEnVol.update(n => n + 1);
        return this._httpClient.get<ParametragesGlobauxClientCalculCommission>(href).pipe(
          map(r => r.parametrages),
          startWith(undefined),
          finalize(() => this.requetesParametragesEnVol.update(n => n - 1)),
        );
      }),
    ),
  );

  parametragesSpecifiquesEvenement = toSignal(
    toObservable(this.clientSelection).pipe(
      filter(c => !!c),
      switchMap((client: Client) => {
        const href = `${environment.backendUrl}redditions/parametrage-calcul-commission/${client.codeClient}/specifique/evenement`;

        this.requetesParametragesEnVol.update(n => n + 1);
        return this._httpClient.get<ParametragesSpecifiquesCalculCommissionEvenement>(href).pipe(
          startWith(undefined),
          finalize(() => this.requetesParametragesEnVol.update(n => n - 1)),
        );
      }),
    ),
  );

  parametragesSpecifiquesAbonnement = toSignal(
    toObservable(this.clientSelection).pipe(
      filter(c => !!c),
      switchMap((client: Client) => {
        const href = `${environment.backendUrl}redditions/parametrage-calcul-commission/${client.codeClient}/specifique/abonnement`;

        this.requetesParametragesEnVol.update(n => n + 1);
        return this._httpClient.get<ParametragesSpecifiquesCalculCommissionAbonnement>(href).pipe(
          startWith(undefined),
          finalize(() => this.requetesParametragesEnVol.update(n => n - 1)),
        );
      }),
    ),
  );

  parametragesSpecifiquesPack = toSignal(
    toObservable(this.clientSelection).pipe(
      filter(c => !!c),
      switchMap((client: Client) => {
        const href = `${environment.backendUrl}redditions/parametrage-calcul-commission/${client.codeClient}/specifique/pack`;

        this.requetesParametragesEnVol.update(n => n + 1);
        return this._httpClient.get<ParametragesSpecifiquesCalculCommissionPack>(href).pipe(
          startWith(undefined),
          finalize(() => this.requetesParametragesEnVol.update(n => n - 1)),
        );
      }),
    ),
  );

  private ajouterParametragesSpecifiquesEvenement = toSignal(
    toObservable(this.parametrageSpecifiqueEvenementAAjouter).pipe(
      filter(Boolean),
      switchMap(parametrage => {
        const endpoint = `${environment.backendUrl}redditions/parametrage-calcul-commission-specifique/evenement`;

        return this._httpClient.post(endpoint, parametrage).pipe(
          tap(_ => {
            this.parametrageSpecifiqueEvenementAAjouter.set(undefined);
            this.clientSelection.set({ ...this.clientSelection()! });
          }),
        );
      }),
    ),
  );

  private supprimerParametrageSpecifiqueEvenement = toSignal(
    toObservable(this.parametrageSpecifiqueEvenementASupprimer).pipe(
      filter(Boolean),
      switchMap(parametrage => {
        const endpoint = `${environment.backendUrl}redditions/parametrage-calcul-commission-specifique/evenement/${parametrage.idEvenement}`;

        return this._httpClient.delete(endpoint).pipe(
          tap(_ => {
            this.parametrageSpecifiqueEvenementASupprimer.set(undefined);
            this.clientSelection.set({ ...this.clientSelection()! });
          }),
        );
      }),
    ),
  );

  private ajouterParametragesSpecifiquesAbonnement = toSignal(
    toObservable(this.parametrageSpecifiqueAbonnementAAjouter).pipe(
      filter(Boolean),
      switchMap(parametrage => {
        const endpoint = `${environment.backendUrl}redditions/parametrage-calcul-commission-specifique/abonnement`;

        return this._httpClient.post(endpoint, parametrage).pipe(
          tap(_ => {
            this.parametrageSpecifiqueAbonnementAAjouter.set(undefined);
            this.clientSelection.set({ ...this.clientSelection()! });
          }),
        );
      }),
    ),
  );

  private supprimerParametrageSpecifiqueAbonnement = toSignal(
    toObservable(this.parametrageSpecifiqueAbonnementASupprimer).pipe(
      filter(Boolean),
      switchMap(parametrage => {
        const endpoint = `${environment.backendUrl}redditions/parametrage-calcul-commission-specifique/abonnement/${parametrage.idFormule}`;

        return this._httpClient.delete(endpoint).pipe(
          tap(_ => {
            this.parametrageSpecifiqueAbonnementASupprimer.set(undefined);
            this.clientSelection.set({ ...this.clientSelection()! });
          }),
        );
      }),
    ),
  );

  private ajouterParametragesSpecifiquesPack = toSignal(
    toObservable(this.parametrageSpecifiquePackAAjouter).pipe(
      filter(Boolean),
      switchMap(parametrage => {
        const endpoint = `${environment.backendUrl}redditions/parametrage-calcul-commission-specifique/pack`;

        return this._httpClient.post(endpoint, parametrage).pipe(
          tap(_ => {
            this.parametrageSpecifiquePackAAjouter.set(undefined);
            this.clientSelection.set({ ...this.clientSelection()! });
          }),
        );
      }),
    ),
  );

  private supprimerParametrageSpecifiquePack = toSignal(
    toObservable(this.parametrageSpecifiquePackASupprimer).pipe(
      filter(Boolean),
      switchMap(parametrage => {
        const endpoint = `${environment.backendUrl}redditions/parametrage-calcul-commission-specifique/pack/${parametrage.idFormule}`;

        return this._httpClient.delete(endpoint).pipe(
          tap(_ => {
            this.parametrageSpecifiquePackASupprimer.set(undefined);
            this.clientSelection.set({ ...this.clientSelection()! });
          }),
        );
      }),
    ),
  );

  recupererLesEvenementsPourParametrage() {
    const href = `${environment.backendUrl}redditions/liste-evenements-parametrage-specifique-calcul-commission/${this.clientSelection()!.codeClient}`;

    return this._httpClient
      .get<Evenement[]>(href)
      .pipe(tap(r => this.evenements.set(r.sort((a, b) => a.idAparte - b.idAparte))));
  }

  recupererLesAbonnementsPourParametrage() {
    const href = `${environment.backendUrl}redditions/liste-abonnements-parametrage-specifique-calcul-commission/${this.clientSelection()!.codeClient}`;

    return this._httpClient
      .get<Abonnement[]>(href)
      .pipe(tap(r => this.abonnements.set(r.sort((a, b) => a.idAparte - b.idAparte))));
  }

  recupererLesPacksPourParametrage() {
    const href = `${environment.backendUrl}redditions/liste-packs-parametrage-specifique-calcul-commission/${this.clientSelection()!.codeClient}`;

    return this._httpClient.get<Pack[]>(href).pipe(tap(r => this.packs.set(r.sort((a, b) => a.idAparte - b.idAparte))));
  }
}
